import type {
  Category,
  PrintArea,
  PrintAreaWithTemplates,
  PrintMethod,
  ProductColor,
  ProductImage,
  TemplateProductImage,
} from '@/features/gfProduct/types';
import type { SaleStatus, Spec } from '@/types';
import type { GfProductSize, GfSizeTableData } from '@/types';

export const PRODUCT_NAME = 'ジャンプLAB';

export const PRODUCT_TYPE = {
  designTool: 'design_tool',
  template: 'template',
  dieCut: 'die_cut',
} as const;
export type ProductTypeType = (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE];

// アイテムオプション選択時のフロー種別
export const PRODUCT_OPTION_FLOW = {
  selectMaterial: 'selectMaterial',
  existingDesign: 'existingDesign',
} as const;

// デザインツールの操作種別
// （GA4 イベント `design_tool_<action>` とPostHog Autocapture の `data-ph-capture-attribute-design-action` の両方で使う）
export const DESIGN_ACTION = {
  switchPart: 'switch-part',
  changeColor: 'change-color',
  selectMaterial: 'select-material',
  deleteMaterial: 'delete-material',
  selectMaterialTab: 'select-material-tab',
  openMaterialModal: 'open-material-modal',
  sizeMax: 'size-max',
  sizeMin: 'size-min',
  rotationReset: 'rotation-reset',
  resetToInitial: 'reset-to-initial',
  clearParts: 'clear-parts',
  canvasZoomIn: 'canvas-zoom-in',
  canvasZoomOut: 'canvas-zoom-out',
  toggleGrid: 'toggle-grid',
  openLogoGuideline: 'open-logo-guideline',
  openPreview: 'open-preview',
  closePreview: 'close-preview',
  back: 'back',
} as const;
export type DesignActionType = (typeof DESIGN_ACTION)[keyof typeof DESIGN_ACTION];

// TOP ヒーローの探索導線。どの入口から探索を始めたかを計測する（GA4 / PostHog の exploreMode）
export const EXPLORE_MODE = {
  /** 「好きな作品を探す」→ 作品ラインナップへスクロール */
  works: 'works',
  /** 「みんなのデザインを見る」→ /user-designs */
  userDesigns: 'userDesigns',
} as const;
export type ExploreModeType = (typeof EXPLORE_MODE)[keyof typeof EXPLORE_MODE];

/**
 * 購入経路。注文単位ではなくカート行/注文明細の1行（＝商品1点）ごとに持つ分類で、
 * 経路別の売上・CVR・再購入を分析するために使う。
 * GA4 / PostHog / DB に同じ文字列で保持する（変換層を作らない）。
 *
 * `cartRoute`（最終経路＝最後にカート投入を起こした方法）と
 * `entryRoute`（元経路＝最初に入った導線）の両方がこの値集合を取る。
 */
export const CART_ROUTE = {
  /** デザインツールで作成してカート投入 */
  designed: 'designed',
  /** 既製・定型デザインを編集せずそのままカート投入 */
  fixedDesign: 'fixedDesign',
  /** テンプレ商品（アクスタ/ステッカー等）から直接カート投入 */
  template: 'template',
  /** 購入履歴からの再購入でカート投入 */
  reorder: 'reorder',
} as const;
export type CartRoute = (typeof CART_ROUTE)[keyof typeof CART_ROUTE];

// クエリ等の未検証文字列が CART_ROUTE の値か判定する。不正値を計測・API に流さないためのガード
export const isCartRoute = (value: string | null): value is CartRoute =>
  value !== null && (Object.values(CART_ROUTE) as string[]).includes(value);

// カート投入・計測に乗せる購入経路（item スコープ）の単一ソース型。
// 各種 Params / CartItem / PurchaseItem はこれを合成して使い、4値の構造を一箇所に保つ。
export type PurchaseRoute = {
  cartRoute: CartRoute;
  entryRoute: CartRoute;
  basedOnUserDesign: boolean;
  fromShareUrl: boolean;
};

// 保存済み・送信途中で一部が未確定の場面（カート行・API パラメータ等）向けの部分型
export type PurchaseRouteFields = Partial<PurchaseRoute>;

export const BREAKPOINT = {
  SP: 800,
};

// デバイスタイプ
export const DEVICE_TYPE = {
  PC: 'pc',
  SP: 'sp',
} as const;

export type DeviceType = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE] | '';

export const PRODUCT_COLORS = ['Skyblue', 'Green', 'Orange', 'Purple', 'Turquoise', 'Yellow'] as const;

// 販売ステータスの定数
export const SALE_STATUS = {
  onSale: 'on_sale',
  discontinued: 'discontinued',
  comingSoon: 'coming_soon',
} as const;

// 販売中以外のとき金額の代わりに表示するラベル（on_sale は金額を表示するため持たない）
// NOTE: discontinued は販売終了ステータスだが表記は在庫切れにする
export const UNAVAILABLE_SALE_STATUS_LABEL = {
  [SALE_STATUS.discontinued]: '在庫切れ',
  [SALE_STATUS.comingSoon]: '近日発売',
} as const satisfies Partial<Record<SaleStatus, string>>;

// 販売終了時の表示メッセージ
export const DISCONTINUED_MESSAGE = 'こちらのアイテムは取り扱いを終了いたしました';

// 商品詳細の共通フィールド
export type BaseProductDetail = {
  id: number;
  name: string;
  description: string;
  basePrice: number;
  taxExcludedPrice: number;
  model3dPath: string;
  texture3dPath: string;
  sizeTable: GfSizeTableData | null;
  displayOrder: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  categories: Category[];
  printMethods: PrintMethod[];
  specs: Spec[];
  saleStatus: SaleStatus;
};

// デザインツール・型抜き商品
export type DesignToolProductDetail = BaseProductDetail & {
  productType: typeof PRODUCT_TYPE.designTool | typeof PRODUCT_TYPE.dieCut;
  printAreas: PrintArea[];
  colors: ProductColor[];
  sizes: GfProductSize[];
  images: ProductImage[];
};

// テンプレ商品（アクスタ・シール）
export type TemplateProductDetail = BaseProductDetail & {
  productType: typeof PRODUCT_TYPE.template;
  templateType: 'sticker' | 'acrylic_stand';
  templateImages: TemplateProductImage[];
  printAreas: PrintAreaWithTemplates[];
};

export type ProductDetail = DesignToolProductDetail | TemplateProductDetail;

// お支払い方法（APIの payment_method に渡す値）
export const PAYMENT_METHOD = {
  credit: 'credit',
  paypay: 'paypay',
} as const;

export type PaymentMethodType = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

// 前回選択決済手段の cookie（購入確認画面のデフォルト表示用）
export const LAST_SELECTED_PAYMENT_METHOD_COOKIE_NAME = 'last-selected-payment-method';

// 発送予定日文言
export const SHIPPING_DATE_TEXT = '発送目安：ご注文から10日〜15日';

// βテストログイン時のcookieキー
// next-authで使用するjwtトークンとは別
export const BETA_AUTH_COOKIE_NAME = 'beta-auth-token';
export const TEASER_HASH_COOKIE_NAME = 'teaser-hash';

// 規約同意フラグのcookieキー
export const USER_TERMS_ACCEPTED_COOKIE_NAME = 'user-terms-accepted';

// 購入前注意事項の表示済みフラグのcookieキー
export const DESIGN_ANNOTATION_COOKIE_NAME = 'showDesignAnnotation';

// 素材カテゴリ
export const MaterialCategory = {
  frame: { value: 'frame', text: 'コマ' },
  logo: { value: 'logo', text: 'ロゴ' },
  text: { value: 'text', text: 'テキスト' },
  character: { value: 'character', text: 'キャラクター' },
} as const;

export type MaterialCategoryType = keyof typeof MaterialCategory;

// 素材の初期配置時の基準サイズ比率
// 表示キャンバスに対して49.6%の大きさ（2つ横並びで重ならないようにするため）
export const DEFAULT_MATERIAL_SIZE_RATIO = 0.496;

// 素材一覧のページネーション用のリミット
export const MATERIAL_PAGE_LIMIT = 100;

// 人気のコマ一覧のリミット
export const MATERIAL_POPULAR_PAGE_LIMIT = 6;

//サービス公式SNS
export const SERVICE_OFFICIAL_SNS_URL_X = 'https://x.com/jumplab_info';
export const SERVICE_OFFICIAL_SNS_URL_INSTAGRAM = 'https://www.instagram.com/jumplab_info/?hl=ja';

// ティーザーLP用YouTube動画ID
export const TEASER_YOUTUBE_VIDEO_ID = 'lG4Z3JTk9nM';

//ジャンプLABの固定ユーザ名
export const JUMPLAB_FIXED_USER_NAME = 'ジャンプLABユーザ';

// HTTPステータスコードとメッセージ
export const ERROR_STATUS = {
  unauthorized: {
    code: 401,
    message: `再度ログインしてください。<br>繰り返しても改善しない場合は<br><a href="/contact">お問い合わせページ</a>にてお問い合わせください。`,
  },
};
